/// <reference types="node" />

import { getDomainSource, SERVICE } from '@musinsa/packages-device-detection';
import { type AxiosInstance, createHttpClient, createHttpInstance } from '@musinsa/packages-http';
import type { HttpInstanceOptions } from './auth';
import { setupAuthInterceptors } from './auth';
import { getCuratorApiBaseURL } from './utils/getCuratorApiBaseURL';
import { setupServicePlatformInterceptor } from './utils/servicePlatformInterceptors';

/**
 * 기본 리프레시 실패 처리: 로그아웃 페이지로 이동
 * @description onRefreshFail이 제공되지 않았을 때 사용되는 기본 동작. 로그아웃 페이지에서 logout API 호출 및 로그인 페이지 리다이렉트(웹뷰 분기 포함)를 담당합니다.
 */
const defaultOnRefreshFail = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const host = window.location.hostname;
  const href = window.location.href;
  const service = getDomainSource(host);
  const url =
    service === SERVICE.MUSINSA
      ? `${process.env.NEXT_PUBLIC_MUSINSA_BASE_URL}/auth/login?referer=${encodeURIComponent(href)}`
      : `${process.env.NEXT_PUBLIC_BASE_URL}/auth/logout?redirect_uri=${encodeURIComponent(href)}`;

  window.location.replace(url);
};

/**
 * HTTP 인스턴스를 생성합니다.
 *
 * 대부분의 API는 모듈에서 export하는 privateHttpClient / publicHttpClient를 사용하면 됩니다.
 * 커스텀 onRefreshFail 등 옵션이 필요할 때만 getHttpInstance()를 직접 사용하세요.
 *
 * @param options - 인스턴스 생성 옵션 (HttpInstanceOptions)
 * @param options.public - true면 인증 불필요 (public API), false/undefined면 인증 필요
 * @param options.onRefreshFail - 리프레시 실패 시 콜백 (기본값: 로그아웃 페이지로 이동)
 * @returns Axios 인스턴스
 *
 * @example
 * ```typescript
 * // 일반적인 경우: export된 클라이언트 사용
 * import { privateHttpClient, publicHttpClient } from './httpClient';
 *
 * // 인증 불필요 API (public)
 * const publicInstance = getHttpInstance({ public: true });
 * const publicClient = createHttpClient(publicInstance);
 *
 * // 인증 필요 API (기본값 - 리프레시 실패 시 로그아웃 페이지로 이동)
 * const authInstance = getHttpInstance();
 * const authClient = createHttpClient(authInstance);
 *
 * // 커스텀 리프레시 실패 핸들러가 필요한 경우에만
 * const authInstance = getHttpInstance({
 *   onRefreshFail: () => customRedirect(),
 * });
 * const authClient = createHttpClient(authInstance);
 * ```
 */

const getHttpInstance = (options?: HttpInstanceOptions): AxiosInstance => {
  const instance = createHttpInstance({
    // NOTE: 기본값으로 29CM API base URL을 사용, setupServicePlatformInterceptor에서 도메인 소스에 따라 변경됩니다.
    baseURL: getCuratorApiBaseURL(SERVICE['29CM']),
    withCredentials: true,
  });

  if (typeof window === 'undefined') {
    return instance;
  }

  const domainSource = getDomainSource(window.location.hostname);
  // 모든 요청에 x-platform 헤더 추가
  setupServicePlatformInterceptor(instance, domainSource);

  // public API는 인증 인터셉터 없이 반환
  if (options?.public || domainSource !== SERVICE['29CM']) {
    return instance;
  }

  // 인증 필요 API: 인터셉터 설정 (클라이언트 전용)
  setupAuthInterceptors(instance, {
    onRefreshFail: options?.onRefreshFail ?? defaultOnRefreshFail,
  });

  return instance;
};

/** 인증이 필요한 API용 HTTP 클라이언트 (401 시 리프레시, 실패 시 로그아웃 페이지 이동) */
export const privateHttpClient = createHttpClient(getHttpInstance());
/** 인증이 필요 없는 API용 HTTP 클라이언트 (인터셉터 없음) */
export const publicHttpClient = createHttpClient(getHttpInstance({ public: true }));
